import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, AlertCircle, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { servicesService } from '../../services/ServicesService';

/**
 * EbookViewer — modal fullscreen que renderiza o PDF de um addon do tipo ebook.
 *
 * Comportamento:
 *  - Busca a URL do PDF no backend (GET /api/admin/addons/public/:addon_id, checa entitlement).
 *  - Se houver PDF → renderiza em <iframe>.
 *  - Se o admin nao subiu o PDF (pdf_url vazio) → fallback: oferece criar/abrir um
 *    ticket de pedido de e-book ('ebook_request') para suporte enviar manualmente.
 */
const EbookViewer = ({ addonId, onClose }) => {
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const res = await api.get(`/api/admin/addons/public/${addonId}`);
        if (cancelled) return;
        const data = res.data?.data || {};
        setPdfUrl(data.pdf_url || '');
        setPdfFileName(data.pdf_file_name || '');
        setName(data.name || '');
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err?.message || 'Não foi possível carregar o e-book.';
        setError(typeof detail === 'string' ? detail : 'Não foi possível carregar o e-book.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [addonId]);

  const handleFallbackTicket = async () => {
    try {
      const formData = new FormData();
      formData.append('service_type', 'ebook_request');
      formData.append('phone', '');
      formData.append('contact_email', '');
      formData.append('observations', `Solicitação de envio manual do e-book (${name || addonId}). O admin ainda não disponibilizou o arquivo PDF.`);
      const res = await servicesService.createTicket(formData);
      const ticket = res?.data;
      if (ticket?.id) {
        navigate(`/services/tickets/${ticket.id}`);
      }
    } catch (err) {
      console.error('Falha ao criar ticket de fallback:', err);
      setError('Não conseguimos abrir o chat agora. Tente novamente em instantes.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs text-gray-400">E-book</p>
          <h2 className="text-base font-bold text-gray-900 truncate">{name || 'Carregando...'}</h2>
        </div>
        <div className="flex items-center gap-2">
          {pdfUrl && (
            <a
              href={pdfUrl}
              download={pdfFileName || true}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Baixar
            </a>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 bg-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="max-w-md text-center bg-white rounded-2xl border border-gray-200 p-8">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">{error}</h3>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        ) : !pdfUrl ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="max-w-md text-center bg-white rounded-2xl border border-gray-200 p-8">
              <div className="w-12 h-12 mx-auto mb-3 bg-amber-50 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">E-book em preparação</h3>
              <p className="text-sm text-gray-500 mb-5">
                O arquivo ainda não foi disponibilizado. Abra um chat com a nossa equipe e o e-book será enviado por lá.
              </p>
              <button
                onClick={handleFallbackTicket}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 rounded-lg transition-all"
              >
                Abrir chat com a equipe
              </button>
            </div>
          </div>
        ) : (
          <iframe
            src={pdfUrl}
            title={name || 'E-book'}
            className="w-full h-full border-0 bg-white"
          />
        )}
      </div>
    </div>
  );
};

export default EbookViewer;
